import React, { useRef, useState, useEffect } from "react";
import { Box, styled, IconButton } from "@mui/material";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import { useTranslation } from "react-i18next";

// Asset URLs from Figma
const img1 = "/images/7bd58dd5150d91b22e62365ce93f12335c86901e.jpg";
const imgImage63 = "/images/8683553b624355c64c1f2f9eb8a9b66585440789.jpg";
const imgImage3 = "/images/7154f9aab0b4ee0f009149d4994caa2d3ad57045.jpg";
const imgImage64 = "/images/7b9a875b7e1b731bc6db96457f27bbea6021b824.jpg";
const imgImage65 = "/images/098230b723cf44b5985277676990b23061b9fafc.jpg";
const img = "/images/6577f3e446075f9c665926bef36ad11e0d56474c.svg";
const imgVector32 = "/images/e8db36a4e5db363f9b39189a67a263a52384bd89.svg";

// Main container with full responsive design
const MainContainer = styled(Box)<{ isRTL?: boolean }>(({ theme, isRTL }) => ({
  boxSizing: "border-box",
  paddingTop: "137px",
  paddingBottom: "137px",
  paddingLeft: isRTL ? "0" : "130px",
  paddingRight: "0",
  position: "relative",
  width: "100%",
  maxWidth: "100%",
  overflow: "hidden",
  [theme.breakpoints.down("lg")]: {
    paddingLeft: "20px",
    paddingRight: "20px",
  },
  [theme.breakpoints.down("md")]: {
    paddingTop: "80px",
    paddingBottom: "80px",
    paddingLeft: "20px",
    paddingRight: "20px",
  },
  [theme.breakpoints.down("sm")]: {
    paddingTop: "0px",
    paddingBottom: "0px",
    paddingLeft: "16px",
    paddingRight: "16px",
  },
}));

// Title section with responsive positioning
const TitleSection = styled(Box)<{ isRTL?: boolean }>(({ theme, isRTL }) => ({
  fontFamily: "'Inter', sans-serif",
  fontWeight: 700,
  lineHeight: "0",
  fontStyle: "normal",
  position: "absolute",
  ...(isRTL ? { right: "60px" } : { left: "130px" }),
  ...(isRTL ? { top: "230px" } : { top: "137px" }),
  zIndex: 2,
  flexShrink: 0,
  color: "#3e4044",
  fontSize: "48px",
  width: "251px",
  [theme.breakpoints.down("lg")]: {
    top: "200px",
    ...(isRTL ? { right: "20px" } : { left: "20px" }),
    fontSize: "40px",
    width: "300px",
  },
  [theme.breakpoints.down("md")]: {
    position: "relative",
    left: "0",
    right: "0",
    top: "0",
    fontSize: "32px",
    width: "100%",
    textAlign: "center",
    marginBottom: "40px",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "22px",
    marginBottom: "4px",
  },
  "& p": {
    lineHeight: "1.5",
    margin: 0,
  },
}));

// Content wrapper with responsive layout
const ContentWrapper = styled(Box)<{ isRTL?: boolean }>(({ theme, isRTL }) => ({
  display: "flex",
  gap: "64px",
  alignItems: "center",
  width: "100%",
  height: "100%",
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
    gap: "0",
    alignItems: "stretch",
  },
}));

// Title spacer with responsive width
const TitleSpacer = styled(Box)(({ theme }) => ({
  width: "251px",
  flexShrink: 0,
  [theme.breakpoints.down("lg")]: {
    width: "300px",
  },
  [theme.breakpoints.down("md")]: {
    display: "none",
  },
}));

// Horizontal scroll container with responsive behavior
const ScrollContainer = styled(Box)<{ isRTL?: boolean }>(({ theme, isRTL }) => ({
  display: "flex",
  gap: "64px",
  alignItems: "center",
  overflowX: "auto",
  overflowY: "visible",
  flex: 1,
  scrollBehavior: "smooth",
  paddingBottom: "12px",
  direction: isRTL ? "rtl" : "ltr",
  paddingRight: isRTL ? "130px" : "0",
  "&::-webkit-scrollbar": {
    display: "none",
  },
  "&": {
    msOverflowStyle: "none",
    scrollbarWidth: "none",
  },
  [theme.breakpoints.down("lg")]: {
    gap: "40px",
    paddingRight: isRTL ? "20px" : "0",
  },
  [theme.breakpoints.down("md")]: {
    gap: "24px",
    paddingRight: "0",
  },
  [theme.breakpoints.down("sm")]: {
    gap: "16px",
  },
}));

// Feature card with full responsive design
const FeatureCard = styled(Box)(({ theme }) => ({
  boxSizing: "border-box",
  display: "flex",
  gap: "48px",
  alignItems: "center",
  justifyContent: "flex-start",
  paddingLeft: "48px",
  paddingRight: "24px",
  paddingTop: "48px",
  paddingBottom: "48px",
  position: "relative",
  borderRadius: "30.818px",
  flexShrink: 0,
  width: "928px",
  minWidth: "928px",
  [theme.breakpoints.down("lg")]: {
    width: "700px",
    minWidth: "700px",
    gap: "32px",
    padding: "32px 20px",
  },
  [theme.breakpoints.down("md")]: {
    width: "100%",
    minWidth: "auto",
    flexDirection: "column",
    textAlign: "center",
    gap: "24px",
    padding: "32px 20px",
    borderRadius: "24px",
  },
  [theme.breakpoints.down("sm")]: {
    gap: "20px",
    padding: "24px 16px",
    borderRadius: "20px",
  },
}));

// Dotted background pattern with responsive sizing
const DottedBackground = styled("img")<{ isRTL?: boolean }>(({ theme, isRTL }) => ({
  position: "absolute",
  ...(isRTL ? { right: "0", transform: "translateY(-50%) scaleX(-1)" } : { left: "0", transform: "translateY(-50%)" }),
  top: "50%",
  width: "263px",
  height: "340px",
  display: "block",
  maxWidth: "none",
  [theme.breakpoints.down("lg")]: {
    width: "200px",
    height: "280px",
  },
  [theme.breakpoints.down("md")]: {
    display: "none", // Hide dotted background on mobile for cleaner look
  },
}));

// Image container with responsive sizing
const ImageContainer = styled(Box)(({ theme }) => ({
  background: "#f5f5f5",
  height: "260px",
  overflow: "hidden",
  position: "relative",
  borderRadius: "48px",
  flexShrink: 0,
  width: "340px",
  [theme.breakpoints.down("lg")]: {
    width: "280px",
    height: "220px",
    borderRadius: "40px",
  },
  [theme.breakpoints.down("md")]: {
    width: "100%",
    maxWidth: "320px",
    height: "200px",
    borderRadius: "32px",
    margin: "0 auto",
  },
  [theme.breakpoints.down("sm")]: {
    height: "180px",
    borderRadius: "24px",
  },
}));

// Text content section with responsive layout
const TextContent = styled(Box)(({ theme }) => ({
  boxSizing: "border-box",
  display: "flex",
  flexDirection: "column",
  gap: "12.327px",
  alignItems: "flex-start",
  justifyContent: "flex-start",
  lineHeight: "0",
  fontStyle: "normal",
  paddingBottom: "12px",
  paddingTop: "0",
  paddingLeft: "0",
  paddingRight: "0",
  position: "relative",
  flexShrink: 0,
  width: "460px",
  [theme.breakpoints.down("lg")]: {
    width: "380px",
  },
  [theme.breakpoints.down("md")]: {
    width: "100%",
    alignItems: "center",
    textAlign: "center",
  },
}));

// Title styling with responsive fonts
const FeatureTitle = styled(Box)(({ theme }) => ({
  fontFamily: "'Inter', sans-serif",
  fontWeight: 600,
  position: "relative",
  flexShrink: 0,
  color: "#3e4044",
  fontSize: "32px",
  whiteSpace: "nowrap",
  [theme.breakpoints.down("lg")]: {
    fontSize: "28px",
  },
  [theme.breakpoints.down("md")]: {
    fontSize: "24px",
    whiteSpace: "normal",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "22px",
  },
  "& p": {
    lineHeight: "1.5",
    whiteSpace: "pre",
    margin: 0,
    [theme.breakpoints.down("md")]: {
      whiteSpace: "normal",
    },
  },
}));

// Description styling with responsive fonts
const FeatureDescription = styled(Box)(({ theme }) => ({
  fontFamily: "'Inter', sans-serif",
  fontWeight: 400,
  minWidth: "100%",
  position: "relative",
  flexShrink: 0,
  color: "#5a5d63",
  fontSize: "18px",
  width: "min-content",
  [theme.breakpoints.down("lg")]: {
    fontSize: "16px",
  },
  [theme.breakpoints.down("md")]: {
    fontSize: "16px",
    width: "100%",
    textAlign: "center",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "14px",
  },
  "& p": {
    lineHeight: "1.5",
    margin: 0,
  },
}));

// Navigation arrow overlay with glassmorphism effect
const NavigationOverlay = styled(IconButton)(({ theme }) => ({
  position: "fixed",
  top: "50%",
  transform: "translateY(-50%)",
  zIndex: 1000,
  width: "64px",
  height: "64px",
  background: "rgba(255, 255, 255, 0.85)",
  backdropFilter: "blur(12px)",
  border: "1px solid rgba(255, 255, 255, 0.2)",
  borderRadius: "20px",
  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1), 0 2px 8px rgba(0, 0, 0, 0.05)",
  color: "#3e4044",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  animation: "pulseIntro 2s ease-out",

  "&:hover": {
    background: "rgba(255, 255, 255, 0.95)",
    transform: "translateY(-50%) scale(1.05)",
    boxShadow: "0 12px 48px rgba(0, 0, 0, 0.15), 0 4px 16px rgba(0, 0, 0, 0.1)",
  },

  "&:active": {
    transform: "translateY(-50%) scale(0.95)",
    transition: "all 0.1s ease",
  },

  "&.bounce-left": {
    animation: "bounceLeft 0.6s ease-out",
  },

  "&.bounce-right": {
    animation: "bounceRight 0.6s ease-out",
  },

  "&.disabled": {
    opacity: 0.4,
    pointerEvents: "none",
    transform: "translateY(-50%) scale(0.9)",
  },

  "& .MuiSvgIcon-root": {
    fontSize: "32px",
    transition: "transform 0.2s ease",
  },

  "&:hover .MuiSvgIcon-root": {
    transform: "scale(1.1)",
  },

  // Hide on tablet and mobile
  [theme.breakpoints.down("lg")]: {
    display: "none",
  },

  // Keyframe animations
  "@keyframes pulseIntro": {
    "0%": {
      opacity: 0,
      transform: "translateY(-50%) scale(0.8)",
    },
    "50%": {
      opacity: 1,
      transform: "translateY(-50%) scale(1.1)",
    },
    "100%": {
      opacity: 1,
      transform: "translateY(-50%) scale(1)",
    },
  },

  "@keyframes bounceLeft": {
    "0%": { transform: "translateY(-50%) translateX(0)" },
    "50%": { transform: "translateY(-50%) translateX(-8px)" },
    "100%": { transform: "translateY(-50%) translateX(0)" },
  },

  "@keyframes bounceRight": {
    "0%": { transform: "translateY(-50%) translateX(0)" },
    "50%": { transform: "translateY(-50%) translateX(8px)" },
    "100%": { transform: "translateY(-50%) translateX(0)" },
  },
}));

// Left arrow positioned next to title
const LeftArrow = styled(NavigationOverlay)<{ isRTL?: boolean }>(({ isRTL }) => ({
  ...(isRTL ? { right: "420px" } : { left: "420px" }), // Positioned after the title section
}));

// Right arrow floating on the right side
const RightArrow = styled(NavigationOverlay)<{ isRTL?: boolean }>(({ isRTL }) => ({
  ...(isRTL ? { left: "32px" } : { right: "32px" }),
}));

const ChooseYourPlan: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [leftArrowAnimation, setLeftArrowAnimation] = useState("");
  const [rightArrowAnimation, setRightArrowAnimation] = useState("");

  // Touch support for mobile devices
  const [touchStartX, setTouchStartX] = useState(0);
  const [touchEndX, setTouchEndX] = useState(0);

  // Check scroll position and update arrow states
  const updateScrollState = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;

      if (isRTL) {
        // In RTL, scrollLeft is negative when scrolled right, 0 when at leftmost
        const maxScrollLeft = -(scrollWidth - clientWidth);
        setCanScrollLeft(scrollLeft < -1); // Can scroll left (visually right) if scrollLeft is negative
        setCanScrollRight(scrollLeft > maxScrollLeft + 1); // Can scroll right (visually left) if not at max negative
      } else {
        setCanScrollLeft(scrollLeft > 0);
        setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
      }
    }
  };

  // Smooth scroll left
  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      // Dynamic scroll distance based on viewport size
      const viewportWidth = window.innerWidth;
      let cardWidth = 928;
      let gap = 64;

      if (viewportWidth < 1200) {
        cardWidth = 700;
        gap = 40;
      }
      if (viewportWidth < 900) {
        cardWidth = 320;
        gap = 24;
      }
      if (viewportWidth < 600) {
        cardWidth = 280;
        gap = 16;
      }

      const scrollDistance = cardWidth + gap;

      scrollContainerRef.current.scrollBy({
        left: isRTL ? scrollDistance : -scrollDistance,
        behavior: "smooth",
      });

      // Add bounce animation
      setLeftArrowAnimation("bounce-left");
      setTimeout(() => setLeftArrowAnimation(""), 600);
    }
  };

  // Smooth scroll right
  const scrollRight = () => {
    if (scrollContainerRef.current) {
      // Dynamic scroll distance based on viewport size
      const viewportWidth = window.innerWidth;
      let cardWidth = 928;
      let gap = 64;

      if (viewportWidth < 1200) {
        cardWidth = 700;
        gap = 40;
      }
      if (viewportWidth < 900) {
        cardWidth = 320;
        gap = 24;
      }
      if (viewportWidth < 600) {
        cardWidth = 280;
        gap = 16;
      }

      const scrollDistance = cardWidth + gap;

      scrollContainerRef.current.scrollBy({
        left: isRTL ? -scrollDistance : scrollDistance,
        behavior: "smooth",
      });

      // Add bounce animation
      setRightArrowAnimation("bounce-right");
      setTimeout(() => setRightArrowAnimation(""), 600);
    }
  };

  // Touch event handlers for mobile swipe support
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStartX(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEndX(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStartX || !touchEndX) return;

    const distance = touchStartX - touchEndX;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe && canScrollRight) {
      scrollRight();
    }
    if (isRightSwipe && canScrollLeft) {
      scrollLeft();
    }
  };

  // Setup scroll listeners
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      // Initial scroll state check
      updateScrollState();

      // Add scroll listener
      container.addEventListener("scroll", updateScrollState);

      return () => {
        container.removeEventListener("scroll", updateScrollState);
      };
    }
  }, [isRTL]);

  // Update scroll state when window resizes or content changes
  useEffect(() => {
    const timer = setTimeout(() => {
      updateScrollState();
    }, 100); // Small delay to ensure DOM is updated

    return () => clearTimeout(timer);
  }, []);

  // Handle window resize to update scroll states
  useEffect(() => {
    const handleResize = () => {
      updateScrollState();
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  const renderCard = (title: string, description: string, imageComponent: React.ReactNode, key: number) => (
    <FeatureCard key={key}>
      <DottedBackground isRTL={isRTL} src={img} alt="" />
      <ImageContainer>{imageComponent}</ImageContainer>
      <TextContent>
        <FeatureTitle>
          <p>{title}</p>
        </FeatureTitle>
        <FeatureDescription>
          <p>{description}</p>
        </FeatureDescription>
      </TextContent>
    </FeatureCard>
  );

  return (
    <MainContainer isRTL={isRTL}>
      <TitleSection isRTL={isRTL}>
        <p>{t("common.chooseYourPlan.mainTitle")}</p>
      </TitleSection>

      <ContentWrapper isRTL={isRTL}>
        <TitleSpacer />
        <ScrollContainer
          isRTL={isRTL}
          ref={scrollContainerRef}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {renderCard(
            t("common.chooseYourPlan.card1Title"),
            t("common.chooseYourPlan.card1Description"),
            <Box
              sx={{
                position: "absolute",
                height: "260px",
                top: "50%",
                transform: "translateX(-50%) translateY(-50%)",
                width: "341.327px",
                left: "calc(50% + 0.5px)",
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  backgroundPosition: "center",
                  backgroundRepeat: "no-repeat",
                  backgroundSize: "cover",
                  inset: "0",
                  backgroundImage: `url('${img1}')`,
                }}
              />
            </Box>,
            1
          )}

          {renderCard(
            t("common.chooseYourPlan.card2Title"),
            t("common.chooseYourPlan.card2Description"),
            <Box
              sx={{
                position: "absolute",
                height: "260px",
                top: "50%",
                transform: "translateX(-50%) translateY(-50%)",
                width: "341.327px",
                left: "calc(50% + 0.5px)",
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  backgroundPosition: "center",
                  backgroundRepeat: "no-repeat",
                  backgroundSize: "cover",
                  inset: "0",
                  backgroundImage: `url('${imgImage63}')`,
                }}
              />
            </Box>,
            2
          )}

          {renderCard(
            t("common.chooseYourPlan.card3Title"),
            t("common.chooseYourPlan.card3Description"),
            <Box
              sx={{
                position: "absolute",
                height: "260px",
                top: "50%",
                transform: "translateX(-50%) translateY(-50%)",
                width: "341.327px",
                left: "calc(50% + 0.5px)",
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  backgroundPosition: "center",
                  backgroundRepeat: "no-repeat",
                  backgroundSize: "cover",
                  inset: "0",
                  backgroundImage: `url('${imgImage3}')`,
                }}
              />
            </Box>,
            3
          )}

          {renderCard(
            t("common.chooseYourPlan.card4Title"),
            t("common.chooseYourPlan.card4Description"),
            <Box
              sx={{
                position: "absolute",
                height: "260px",
                top: "50%",
                transform: "translateX(-50%) translateY(-50%)",
                width: "341.327px",
                left: "calc(50% + 0.5px)",
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  backgroundPosition: "center",
                  backgroundRepeat: "no-repeat",
                  backgroundSize: "cover",
                  inset: "0",
                  backgroundImage: `url('${imgImage64}')`,
                }}
              />
            </Box>,
            4
          )}

          {renderCard(
            t("common.chooseYourPlan.card5Title"),
            t("common.chooseYourPlan.card5Description"),
            <Box
              sx={{
                position: "absolute",
                height: "260px",
                top: "50%",
                transform: "translateX(-50%) translateY(-50%)",
                width: "341.327px",
                left: "calc(50% + 0.5px)",
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  backgroundPosition: "center",
                  backgroundRepeat: "no-repeat",
                  backgroundSize: "cover",
                  inset: "0",
                  backgroundImage: `url('${imgImage65}')`,
                }}
              />
            </Box>,
            5
          )}
        </ScrollContainer>
      </ContentWrapper>

      {/* Navigation Overlay Arrows */}
      <LeftArrow
        isRTL={isRTL}
        className={`${leftArrowAnimation} ${!canScrollLeft ? "disabled" : ""}`}
        onClick={scrollLeft}
        aria-label={t("common.chooseYourPlan.scrollLeftLabel")}
      >
        {isRTL ? <ChevronRight /> : <ChevronLeft />}
      </LeftArrow>

      <RightArrow
        isRTL={isRTL}
        className={`${rightArrowAnimation} ${!canScrollRight ? "disabled" : ""}`}
        onClick={scrollRight}
        aria-label={t("common.chooseYourPlan.scrollRightLabel")}
      >
        {isRTL ? <ChevronLeft /> : <ChevronRight />}
      </RightArrow>
    </MainContainer>
  );
};

export default ChooseYourPlan;
